import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, CheckCircle, ArrowRight, ShieldCheck, UploadSimple, IdentificationCard, ClockClockwise, Warning, Eye, EyeSlash, Fingerprint } from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";
import { processAndUploadImage } from "../lib/imageUpload";
import { isNative } from "../lib/native";
import { PhotoSourceSheet } from "./PhotoSourceSheet";
import { useAuth } from "../lib/auth";
import { useAuthModal } from "../lib/authModal";
import { isValidCPF, isValidCNPJ, onlyDigits } from "../lib/br-doc";
import { getClientIp } from "../lib/clientIp";
import { biometricAvailable, isBiometricOptedIn } from "../lib/biometric";
import { getGeoPosition, checkGeoPermission } from "../lib/geolocation";
import { KycWhyLink } from "./KycWhyModal";

// V12.7: captura geolocalização (best-effort) p/ registro legal do aceite dos termos
// V18.9.5: usa wrapper (plugin nativo em Android/iOS empacotado, navegador no web)
// V18.9.12: NÃO dispara prompt de permissão no cadastro/login — só usa o GPS se o
// usuário já autorizou antes (evita o diálogo do sistema em momento inoportuno).
async function getGeo() {
  try {
    const perm = await checkGeoPermission();
    if (perm !== "granted") return null;
  } catch { return null; }
  return getGeoPosition({ enableHighAccuracy: false, timeout: 6000, maximumAge: 300000 })
    .then((pos) => ({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }))
    .catch(() => null);
}

// Campo de senha com botão "olho" para mostrar/ocultar a senha digitada.
function PasswordField({ value, onChange, placeholder, testId, required = true, autoComplete = "current-password", name = "password" }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input
        required={required}
        type={show ? "text" : "password"}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        className="aj-input pr-11"
        data-testid={testId}
        autoComplete={autoComplete}
        name={name}
        id={name}
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-[#004E98]"
        aria-label={show ? "Ocultar senha" : "Mostrar senha"}
        data-testid={`${testId}-toggle`}
      >
        {show ? <EyeSlash size={20} /> : <Eye size={20} />}
      </button>
    </div>
  );
}


function maskCPF(v) {
  const d = v.replace(/\D/g, "").slice(0, 11);
  return d
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
}
function maskCNPJ(v) {
  const d = v.replace(/\D/g, "").slice(0, 14);
  return d
    .replace(/(\d{2})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1/$2")
    .replace(/(\d{4})(\d)/, "$1-$2");
}
function maskPhone(v) {
  const d = v.replace(/\D/g, "").slice(0, 11);
  if (d.length <= 10) {
    return d.replace(/(\d{2})(\d{4})(\d)/, "($1) $2-$3");
  }
  return d.replace(/(\d{2})(\d{5})(\d)/, "($1) $2-$3");
}

export default function AuthModal() {
  const { open, close, completed } = useAuthModal();
  const { user, login, register, refresh, enableBiometric } = useAuth();
  const [mode, setMode] = useState("login"); // 'login' | 'register'
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  // V17.9.9 — onboarding biométrico (aparece uma vez após login bem-sucedido)
  const [bioPromptType, setBioPromptType] = useState(null); // null | "FACE_ID" | "TOUCH_ID" | "GENERIC"

  // form state
  const [form, setForm] = useState({
    name: "", email: "", cpf: "", phone: "", password: "", doc_type: "cpf", razao_social: "", nome_fantasia: "",
    referral_code: (() => { try { return localStorage.getItem("aj_ref_code") || ""; } catch { return ""; } })(),
  });
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [otp, setOtp] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [cnpjLoading, setCnpjLoading] = useState(false);

  // KYC step 3 (unificado com EditProfile)
  const [kycType, setKycType] = useState("");
  const [kycNumber, setKycNumber] = useState("");
  const [kycFrontPath, setKycFrontPath] = useState("");
  const [kycBackPath, setKycBackPath] = useState("");
  const [kycUploading, setKycUploading] = useState({ front: false, back: false });
  const [kycSheet, setKycSheet] = useState(null); // "front" | "back" | null

  // Decide initial step based on current user status when modal opens
  useEffect(() => {
    if (!open) return;
    if (!user) { setMode("login"); setStep(1); return; }
    if (!user.is_verificado_otp) { setMode("register"); setStep(2); }
    else if (user.status !== "ativo_verificado") { setMode("register"); setStep(3); }
    else { completed(); }
    // eslint-disable-next-line
  }, [open, user]);

  // Set default doc type for KYC based on PF/PJ
  useEffect(() => {
    if (!user) return;
    if (kycType) return; // não sobrescreve seleção manual
    if (user.doc_type === "cnpj") setKycType("cartao_cnpj");
    else setKycType(user.kyc_doc_type || "rg");
    // eslint-disable-next-line
  }, [user, step]);

  // V11.9 — pré-preenche o nº do documento com o CPF/CNPJ já cadastrado (evita digitar 2x)
  useEffect(() => {
    if (!user?.cpf) return;
    const masked = user.doc_type === "cnpj" ? maskCNPJ(user.cpf) : maskCPF(user.cpf);
    if (kycType === "cartao_cnpj" || kycType === "cpf") {
      setKycNumber(masked);
    } else if (kycType === "rg" || kycType === "cnh") {
      // limpa se estava preenchido automaticamente com o CPF (RG/CNH têm número próprio)
      setKycNumber((prev) => (prev === masked ? "" : prev));
    }
    // eslint-disable-next-line
  }, [kycType, user]);

  if (!open) return null;

  const upd = (k) => (e) => {
    let v = e.target.value;
    if (k === "cpf") v = form.doc_type === "cnpj" ? maskCNPJ(v) : maskCPF(v);
    if (k === "phone") v = maskPhone(v);
    setForm({ ...form, [k]: v });
  };

  // V12.0 — consulta o CNPJ na Receita (BrasilAPI) e auto-preenche Razão Social / Nome Fantasia
  const fetchCnpj = async (digits) => {
    if (!isValidCNPJ(digits)) return;
    setCnpjLoading(true);
    try {
      const { data } = await api.get(`/cnpj/${digits}`);
      setForm((f) => ({
        ...f,
        razao_social: data.razao_social || f.razao_social,
        nome_fantasia: data.nome_fantasia || f.nome_fantasia,
      }));
      if (data.razao_social) toast.success("Dados da empresa preenchidos automaticamente.");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.info(typeof d === "string" ? d : "Não consegui buscar o CNPJ. Preencha os dados manualmente.");
    } finally {
      setCnpjLoading(false);
    }
  };

  const onCpfChange = (e) => {
    const isCnpj = form.doc_type === "cnpj";
    const masked = isCnpj ? maskCNPJ(e.target.value) : maskCPF(e.target.value);
    setForm((f) => ({ ...f, cpf: masked }));
    if (isCnpj && onlyDigits(masked).length === 14) fetchCnpj(onlyDigits(masked));
  };

  // Step 1: Register
  const submitStep1 = async (e) => {
    e.preventDefault();
    if (!acceptTerms) {
      toast.error("Aceite os Termos de Uso e Política de Pagamentos para continuar");
      return;
    }
    const docDigits = form.cpf.replace(/\D/g, "");
    if (form.doc_type === "cnpj") {
      if (!isValidCNPJ(docDigits)) { toast.error("CNPJ inválido. Confira os números."); return; }
    } else if (!isValidCPF(docDigits)) {
      toast.error("CPF inválido. Confira os números."); return;
    }
    setBusy(true);
    try {
      // V18.9.13 — a coleta de IP/GPS é BEST-EFFORT e NUNCA pode bloquear o cadastro.
      // Era aqui que o botão ficava preso em "Criando..." para sempre: no app nativo o
      // `requestPermissions()` do plugin de localização podia nunca resolver e este
      // `await` travava antes mesmo do POST /auth/register acontecer.
      const meta = await Promise.race([
        Promise.all([getClientIp(), getGeo()]),
        new Promise((resolve) => setTimeout(() => resolve([null, null]), 6000)),
      ]);
      const [client_ip, geo] = meta || [null, null];
      const payload = {
        name: form.name,
        email: form.email.toLowerCase().trim(),
        cpf: form.cpf.replace(/\D/g, ""),
        phone: form.phone.replace(/\D/g, ""),
        password: form.password,
        accept_terms: true,
        doc_type: form.doc_type,
        razao_social: form.doc_type === "cnpj" ? form.razao_social : undefined,
        nome_fantasia: form.doc_type === "cnpj" ? (form.nome_fantasia || undefined) : undefined,
        client_ip: client_ip || undefined,
        geo_lat: geo?.lat,
        geo_lng: geo?.lng,
        geo_accuracy: geo?.accuracy,
        referral_code: (() => { const c = (form.referral_code || "").trim().toUpperCase(); return c || undefined; })(),
      };
      const r = await register(payload);
      if (!r.ok) { toast.error(r.error); return; }
      try { localStorage.removeItem("aj_ref_code"); } catch { /* noop */ }
      toast.success("Cadastro iniciado! Enviando código...");
      await sendOtp();
      setStep(2);
    } catch (err) {
      // Qualquer falha inesperada precisa aparecer — antes o botão ficava travado.
      console.error("[auth] register failed:", err);
      toast.error("Não conseguimos criar sua conta agora. Verifique sua conexão e tente novamente.");
    } finally {
      setBusy(false);
    }
  };

  // Login fallback
  const submitLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    const r = await login(loginForm.email.toLowerCase().trim(), loginForm.password);
    setBusy(false);
    if (!r.ok) { toast.error(r.error); return; }
    // Isso busca os dados do usuário logado e atualiza o estado global
    await refresh();
    const u = r.user;
    if (!u.is_verificado_otp) { await sendOtp(); setStep(2); setMode("register"); return; }
    if (u.status !== "ativo_verificado") { setStep(3); setMode("register"); return; }
    // V17.9.9 — onboarding biométrico: se o device tem Face ID/Touch ID/digital e o
    // usuário ainda não optou, oferece ativar ANTES de fechar o modal (só uma vez por sessão).
    try {
      if (!isBiometricOptedIn() && sessionStorage.getItem("aj_bio_prompt_skipped") !== "1") {
        const { available, biometryType } = await biometricAvailable();
        if (available) {
          setBioPromptType(biometryType || "GENERIC");
          toast.success(`Bem-vindo, ${u.display_name || u.name}!`);
          return; // NÃO fecha ainda — mostra o step do biometric onboarding
        }
      }
    } catch (_e) { /* ignore */ }
    completed();
    toast.success(`Bem-vindo, ${u.display_name || u.name}!`);
  };

  // V17.9.9 — Handler do onboarding biométrico
  const acceptBioPrompt = async () => {
    setBusy(true);
    const r = await enableBiometric();
    setBusy(false);
    if (r.ok) toast.success("Face ID/Touch ID ativado! Da próxima vez, use sua biometria para entrar.");
    else toast.error(r.error || "Não foi possível ativar a biometria agora. Você pode ativar depois em Perfil.");
    setBioPromptType(null);
    completed();
  };
  const declineBioPrompt = () => {
    try { sessionStorage.setItem("aj_bio_prompt_skipped", "1"); } catch { /* ignore */ }
    setBioPromptType(null);
    completed();
  };

  const sendOtp = async () => {
    try {
      const { data } = await api.post("/auth/otp/send");
      // V18.9.13 — o backend agora informa a verdade sobre a entrega. Antes dizia
      // "Código enviado!" mesmo quando o e-mail falhava e o usuário ficava esperando.
      if (data?.email_sent === false) {
        toast.warning("Não conseguimos enviar o e-mail com o código", {
          description: "Confira se o e-mail digitado está correto. Se estiver, toque em \"Reenviar código\" em alguns instantes ou fale com o suporte.",
          duration: 10000,
        });
        return;
      }
      toast.success("Código enviado!", {
        description: "Enviamos um código de 6 dígitos para o seu e-mail. Confira a caixa de entrada (e o spam).",
        duration: 8000,
      });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/otp/verify", { code: otp.trim() });
      await refresh();
      toast.success("Telefone e e-mail verificados!");
      setStep(3);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const uploadKycFile = async (file, side) => {
    if (!file) return;
    setKycUploading((s) => ({ ...s, [side]: true }));
    try {
      // V18.9.6 — kind:"kyc" marca o arquivo como PRIVADO no backend
      const data = await processAndUploadImage(file, { kind: "kyc" });
      if (side === "front") setKycFrontPath(data.path);
      else setKycBackPath(data.path);
      toast.success(side === "front" ? "Foto da frente enviada!" : "Foto do verso enviada!");
    } catch (err) {
      toast.error(err.message || "Falha no upload. Tente outra foto.");
    } finally {
      setKycUploading((s) => ({ ...s, [side]: false }));
    }
  };

  // App nativo: abre o menu padronizado (Câmera/Galeria/Arquivo) ao invés do seletor do WebView.
  const captureKyc = (side) => setKycSheet(side);

  const submitKyc = async (e) => {
    e.preventDefault();
    if (!kycType) { toast.error("Selecione o tipo do documento"); return; }
    if (!kycNumber.trim()) { toast.error("Informe o número do documento"); return; }
    if (!kycFrontPath) { toast.error("Envie a foto do documento"); return; }
    setBusy(true);
    try {
      await api.post("/auth/kyc/submit", {
        doc_type: kycType,
        doc_number: kycNumber.trim(),
        doc_front: kycFrontPath,
        doc_back: kycBackPath || undefined,
      });
      await refresh();
      toast.success("Documento enviado! Aguardando aprovação do suporte.");
      // Não chama completed() porque o usuário ainda NÃO é ativo_verificado.
      // O modal vai re-renderizar com a tela de "aguardando" no próximo open.
      close();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end md:items-center md:justify-center pb-safe"
      onClick={(e) => e.target === e.currentTarget && close()}
      data-testid="auth-modal-overlay"
    >
      <div
        className="bg-white w-full md:max-w-md md:rounded-3xl rounded-t-3xl p-6 max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="auth-modal"
      >
        <div className="flex items-center justify-between mb-1">
          <span className="aj-badge">{bioPromptType ? "Quase lá" : `Passo ${step}/3`}</span>
          <button onClick={bioPromptType ? declineBioPrompt : close} data-testid="close-auth-modal" aria-label="Fechar">
            <X size={22} weight="bold" />
          </button>
        </div>

        {/* Progress — oculta durante o onboarding biométrico */}
        {!bioPromptType && (
          <div className="flex gap-1.5 mt-3 mb-5">
            {[1, 2, 3].map((n) => (
              <div
                key={n}
                className={`h-1.5 flex-1 rounded-full ${n <= step ? "bg-[#FF6B35]" : "bg-slate-200"}`}
              />
            ))}
          </div>
        )}

        {/* V17.9.9 — Onboarding biométrico (aparece após login bem-sucedido em device nativo) */}
        {bioPromptType && (
          <div className="space-y-4 text-center" data-testid="auth-bio-onboarding">
            <div className="w-20 h-20 rounded-full bg-emerald-100 mx-auto flex items-center justify-center">
              <Fingerprint size={40} weight="duotone" className="text-emerald-600" />
            </div>
            <h2 className="font-heading text-2xl font-black">
              {bioPromptType === "FACE_ID" ? "Ativar Face ID?" : bioPromptType === "TOUCH_ID" ? "Ativar Touch ID?" : "Ativar biometria?"}
            </h2>
            <p className="text-sm text-slate-600 leading-relaxed px-2">
              Da próxima vez você abre o AlugaJá sem digitar sua senha —
              é só {bioPromptType === "FACE_ID" ? "olhar pra câmera" : "tocar o sensor"}.
              Suas credenciais ficam guardadas no {bioPromptType === "FACE_ID" ? "Face ID" : "sensor"} do seu aparelho, criptografadas.
            </p>
            <div className="space-y-2 pt-2">
              <button
                type="button"
                onClick={acceptBioPrompt}
                disabled={busy}
                className="btn-primary w-full"
                data-testid="bio-onboarding-accept"
              >
                {busy ? "Ativando..." : `Ativar ${bioPromptType === "FACE_ID" ? "Face ID" : bioPromptType === "TOUCH_ID" ? "Touch ID" : "biometria"}`}
              </button>
              <button
                type="button"
                onClick={declineBioPrompt}
                className="btn-ghost w-full text-sm"
                data-testid="bio-onboarding-decline"
              >
                Agora não
              </button>
            </div>
            <p className="text-[11px] text-slate-400">Você pode ativar/desativar em Perfil a qualquer momento.</p>
          </div>
        )}

        {!bioPromptType && step === 1 && mode === "register" && (
          <form onSubmit={submitStep1} className="space-y-3" data-testid="auth-step-1">
            <h2 className="font-heading text-2xl font-black">Crie sua conta</h2>
            <p className="text-sm text-slate-600 mb-2">Rápido e seguro. Pessoa Física ou Jurídica.</p>

            {/* Toggle CPF / CNPJ */}
            <div className="flex gap-2" data-testid="doc-type-toggle">
              {[{ k: "cpf", label: "Pessoa Física" }, { k: "cnpj", label: "Pessoa Jurídica" }].map((opt) => (
                <button
                  key={opt.k}
                  type="button"
                  onClick={() => setForm({ ...form, doc_type: opt.k, cpf: "" })}
                  className={`flex-1 px-3 py-2 rounded-xl border-2 text-xs font-bold ${form.doc_type === opt.k ? "border-[#004E98] bg-[#004E98]/5 text-[#004E98]" : "border-slate-200 text-slate-500"}`}
                  data-testid={`doc-type-${opt.k}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <input required placeholder={form.doc_type === "cnpj" ? "Nome do responsável" : "Nome completo"} value={form.name} onChange={upd("name")} className="aj-input" data-testid="reg-name" />
            {form.doc_type === "cnpj" && (
              <>
                <div>
                  <input required placeholder="CNPJ" value={form.cpf} onChange={onCpfChange} className="aj-input" inputMode="numeric" data-testid="reg-cpf" />
                  {cnpjLoading && <span className="text-[11px] text-[#004E98] pl-1">Buscando dados do CNPJ na Receita…</span>}
                </div>
                <input required placeholder="Razão Social" value={form.razao_social} onChange={(e) => setForm({ ...form, razao_social: e.target.value })} className="aj-input" data-testid="reg-razao-social" />
                <div>
                  <input placeholder="Nome Fantasia (opcional)" value={form.nome_fantasia} onChange={(e) => setForm({ ...form, nome_fantasia: e.target.value })} className="aj-input" data-testid="reg-nome-fantasia" />
                  <span className="text-[11px] text-slate-400 pl-1">Conforme o Cartão CNPJ. É o nome que aparecerá no app.</span>
                </div>
              </>
            )}
            <input required type="email" placeholder="E-mail" value={form.email} onChange={upd("email")} className="aj-input" data-testid="reg-email" />
            {form.doc_type === "cpf" && (
              <input required placeholder="CPF" value={form.cpf} onChange={onCpfChange} className="aj-input" inputMode="numeric" data-testid="reg-cpf" />
            )}
            <input required placeholder="WhatsApp (DDD + número)" value={form.phone} onChange={upd("phone")} className="aj-input" inputMode="tel" data-testid="reg-phone" />
            <PasswordField value={form.password} onChange={upd("password")} placeholder="Senha (mín. 6)" testId="reg-password" />

            <div>
              <input placeholder="Código de indicação (opcional)" value={form.referral_code}
                onChange={(e) => setForm({ ...form, referral_code: e.target.value.toUpperCase().slice(0, 16) })}
                className="aj-input uppercase" data-testid="reg-referral-code" />
              <p className="text-[11px] text-slate-400 mt-1 px-1">Tem um código de um amigo? Insira aqui e vocês ganham juntos. 🎁</p>
            </div>

            <label className="flex items-start gap-2 text-xs text-slate-700 cursor-pointer pt-1" data-testid="register-terms-label">
              <input
                type="checkbox"
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-[#FF6B35]"
                data-testid="register-accept-terms"
              />
              <span>
                Li e aceito os{" "}
                <a href="/termos" target="_blank" rel="noreferrer" className="text-[#004E98] underline font-bold">Termos de Uso</a>
                {" "}e a{" "}
                <a href="/termos" target="_blank" rel="noreferrer" className="text-[#004E98] underline font-bold">Política de Pagamentos</a>.
              </span>
            </label>

            <button type="submit" disabled={busy || !acceptTerms} className="btn-primary w-full disabled:opacity-50" data-testid="reg-submit">
              {busy ? "Criando..." : "Continuar"} <ArrowRight size={18} weight="bold" />
            </button>

            <p className="text-sm text-slate-600 text-center">
              Já tem conta?{" "}
              <button type="button" onClick={() => setMode("login")} className="font-bold text-[#004E98]" data-testid="switch-to-login">
                Entrar
              </button>
            </p>
          </form>
        )}

        {!bioPromptType && step === 1 && mode === "login" && (
          <form onSubmit={submitLogin} className="space-y-3" data-testid="auth-login" autoComplete="on" method="post">
            <h2 className="font-heading text-2xl font-black">Bem-vindo de volta</h2>
            <input
              required
              type="email"
              placeholder="E-mail"
              value={loginForm.email}
              onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
              className="aj-input"
              data-testid="login-email"
              autoComplete="username email"
              name="username"
              id="username"
              inputMode="email"
            />
            <PasswordField value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} placeholder="Senha" testId="login-password" autoComplete="current-password" name="password" />
            <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="login-submit">
              {busy ? "Entrando..." : "Entrar"}
            </button>
            <p className="text-sm text-slate-600 text-center">
              Novo aqui?{" "}
              <button type="button" onClick={() => setMode("register")} className="font-bold text-[#004E98]" data-testid="switch-to-register">
                Criar conta
              </button>
            </p>
            <p className="text-sm text-slate-600 text-center">
              <a href="#/esqueci-senha" className="font-bold text-[#FF6B35]" data-testid="forgot-password-link" onClick={close}>
                Esqueci minha senha
              </a>
            </p>
          </form>
        )}

        {!bioPromptType && step === 2 && (
          <form onSubmit={submitOtp} className="space-y-4" data-testid="auth-step-2">
            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-[#004E98]/10 mx-auto">
              <ShieldCheck size={32} weight="duotone" color="#004E98" />
            </div>
            <h2 className="font-heading text-2xl font-black text-center">Confirme seu código</h2>
            <p className="text-sm text-slate-600 text-center">
              Enviamos um código de 6 dígitos para o seu e-mail. Confira a caixa de entrada (e o spam) e insira-o abaixo.
            </p>
            <input
              required
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="000000"
              className="aj-input text-center text-2xl tracking-[0.4em] font-bold"
              inputMode="numeric"
              data-testid="otp-input"
            />
            <button type="submit" disabled={busy || otp.length !== 6} className="btn-primary w-full" data-testid="otp-submit">
              {busy ? "Verificando..." : "Verificar"}
            </button>
            <button type="button" onClick={sendOtp} className="w-full text-sm text-[#004E98] font-semibold" data-testid="resend-otp">
              Reenviar código
            </button>
          </form>
        )}

        {!bioPromptType && step === 3 && (() => {
          const kycStatus = user?.kyc_status || (user?.doc_url ? "aguardando_validacao" : "pending");
          const isPJ = user?.doc_type === "cnpj";
          const docOptions = isPJ
            ? [{ v: "cartao_cnpj", label: "Cartão CNPJ" }]
            : [
                { v: "cpf", label: "CPF" },
                { v: "rg", label: "RG" },
                { v: "cnh", label: "CNH" },
              ];

          // Estado: aguardando aprovação (já enviou doc)
          if (kycStatus === "aguardando_validacao") {
            return (
              <div className="space-y-4" data-testid="auth-step-3-waiting">
                <div className="flex items-center justify-center w-16 h-16 rounded-full bg-amber-100 mx-auto">
                  <ClockClockwise size={32} weight="duotone" color="#D97706" />
                </div>
                <h2 className="font-heading text-2xl font-black text-center">Cadastro em análise</h2>
                <p className="text-sm text-slate-600 text-center">
                  Seu documento foi enviado e está sendo revisado pela nossa equipe.
                  Você receberá um e-mail quando for aprovado.
                </p>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800 text-center">
                  Esse processo costuma levar até 24h em horário comercial.
                </div>
                <button
                  type="button"
                  onClick={close}
                  className="btn-primary w-full"
                  data-testid="kyc-waiting-ok"
                >
                  Entendi
                </button>
              </div>
            );
          }

          // Estado: rejeitado → mostra motivo + form de reenvio
          // Estado: pending → mostra form normal
          return (
            <form onSubmit={submitKyc} className="space-y-3" data-testid="auth-step-3">
              <div className="flex items-center justify-center w-16 h-16 rounded-full bg-[#FF6B35]/10 mx-auto">
                <IdentificationCard size={32} weight="duotone" color="#FF6B35" />
              </div>
              <h2 className="font-heading text-2xl font-black text-center">Validar identidade</h2>
              <p className="text-sm text-slate-600 text-center">
                {isPJ
                  ? "Envie uma foto do Cartão CNPJ da empresa."
                  : "Envie uma foto do seu RG ou CNH."}
              </p>
              <div className="text-center">
                <KycWhyLink testId="kyc-why-auth" />
              </div>

              {kycStatus === "rejected" && user?.kyc_rejection_reason && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-xs text-red-800 flex gap-2" data-testid="kyc-reject-reason">
                  <Warning size={16} weight="fill" className="shrink-0 mt-0.5" />
                  <div>
                    <b>Documento rejeitado.</b><br />
                    Motivo: {user.kyc_rejection_reason}
                  </div>
                </div>
              )}

              {/* Tipo do documento */}
              <div>
                <span className="text-[11px] uppercase font-bold tracking-wider text-slate-500">Tipo do documento</span>
                <div className="flex gap-2 mt-1" data-testid="kyc-type-toggle">
                  {docOptions.map((opt) => (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => setKycType(opt.v)}
                      className={`flex-1 px-3 py-2 rounded-xl border-2 text-xs font-bold ${kycType === opt.v ? "border-[#004E98] bg-[#004E98]/5 text-[#004E98]" : "border-slate-200 text-slate-500"}`}
                      data-testid={`kyc-type-${opt.v}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Número */}
              <label className="block">
                <span className="text-[11px] uppercase font-bold tracking-wider text-slate-500">Número do documento</span>
                <input
                  value={kycNumber}
                  onChange={(e) => setKycNumber(e.target.value)}
                  placeholder={kycType === "cartao_cnpj" ? "00.000.000/0000-00" : (kycType === "cnh" ? "00000000000" : "00.000.000-0")}
                  className="aj-input mt-1"
                  data-testid="kyc-doc-number"
                />
              </label>

              {/* Foto frente */}
              <label className={`block border-2 border-dashed rounded-xl p-4 text-center cursor-pointer relative overflow-hidden ${kycFrontPath ? "border-emerald-400 bg-emerald-50" : "border-slate-300 hover:border-[#004E98]"}`} data-testid="kyc-upload-front-label">
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  onClick={(e) => { if (isNative()) { e.preventDefault(); captureKyc("front"); } }}
                  onChange={(e) => uploadKycFile(e.target.files?.[0], "front")}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                  data-testid="kyc-upload-front"
                  disabled={kycUploading.front}
                />
                {kycUploading.front ? (
                  <>
                    <UploadSimple size={24} weight="bold" className="mx-auto text-[#004E98] animate-pulse" />
                    <div className="text-sm font-bold mt-2">Enviando foto...</div>
                  </>
                ) : kycFrontPath ? (
                  <>
                    <CheckCircle size={24} weight="fill" className="mx-auto text-emerald-600" />
                    <div className="text-sm font-bold mt-2 text-emerald-700">Foto enviada ✓</div>
                    <div className="text-[11px] text-slate-500 mt-1">Toque para trocar</div>
                  </>
                ) : (
                  <>
                    <UploadSimple size={24} weight="bold" className="mx-auto text-[#004E98]" />
                    <div className="text-sm font-bold mt-2">Toque para enviar a frente</div>
                    <div className="text-[11px] text-slate-500 mt-1">JPG, PNG ou PDF — até 10MB</div>
                  </>
                )}
              </label>

              {/* Foto verso (opcional p/ PF, principal p/ PJ é o mesmo) */}
              {!isPJ && (
                <label className={`block border-2 border-dashed rounded-xl p-3 text-center cursor-pointer relative overflow-hidden ${kycBackPath ? "border-emerald-400 bg-emerald-50" : "border-slate-300 hover:border-[#004E98]"}`} data-testid="kyc-upload-back-label">
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    onClick={(e) => { if (isNative()) { e.preventDefault(); captureKyc("back"); } }}
                    onChange={(e) => uploadKycFile(e.target.files?.[0], "back")}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                    data-testid="kyc-upload-back"
                    disabled={kycUploading.back}
                  />
                  {kycUploading.back ? (
                    <div className="text-sm font-bold text-slate-600">Enviando verso...</div>
                  ) : kycBackPath ? (
                    <div className="text-sm font-bold text-emerald-700 flex items-center justify-center gap-2">
                      <CheckCircle size={16} weight="fill" /> Verso enviado ✓
                    </div>
                  ) : (
                    <div className="text-[12px] font-bold text-slate-600">Foto do verso (opcional)</div>
                  )}
                </label>
              )}

              <button
                type="submit"
                disabled={busy || !kycFrontPath || !kycNumber.trim() || kycUploading.front || kycUploading.back}
                className="btn-primary w-full disabled:opacity-50"
                data-testid="kyc-submit"
              >
                <CheckCircle size={18} weight="bold" />
                {busy ? "Enviando..." : "Enviar para validação"}
              </button>

              <p className="text-[11px] text-slate-500 text-center">
                Termos já aceitos no cadastro. Sua identidade será revisada em até 24h.
              </p>
            </form>
          );
        })()}
      </div>
      <PhotoSourceSheet
        open={!!kycSheet}
        allowFile
        onClose={() => setKycSheet(null)}
        onPick={(files) => { if (files?.[0]) uploadKycFile(files[0], kycSheet); }}
      />
    </div>
  );
}
